import React, { useMemo } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { ColorPicker } from '../ui/color-picker'
import { Info } from 'lucide-react'
import { BadgeTemplate } from '../../endpoints/BadgeTemplateEndpoints'

export interface OverridableProperty {
  elementId: string
  property: string
  label: string
  currentValue: string
}

/**
 * Parses a BadgeTemplate's layoutDefinition and returns
 * the list of properties marked as overridable.
 */
export function extractOverridableProperties(template: BadgeTemplate | null): OverridableProperty[] {
  if (!template?.isLayoutBased || !template.layoutDefinition) return []
  try {
    const layout = template.layoutDefinition
    const props: OverridableProperty[] = []
    for (const el of layout.elements) {
      if (el.overrides && el.overrides.length > 0) {
        for (const override of el.overrides) {
          props.push({
            elementId: el.id,
            property: override.property,
            label: override.label,
            currentValue: String((el as any)[override.property] ?? ''),
          })
        }
      }
    }
    return props
  } catch {
    return []
  }
}

interface BadgePropertyOverridesProps {
  template: BadgeTemplate | null
  overrides: Record<string, string>
  onChange: (overrides: Record<string, string>) => void
  disabled?: boolean
}

export const BadgePropertyOverrides: React.FC<BadgePropertyOverridesProps> = ({
  template,
  overrides,
  onChange,
  disabled = false,
}) => {
  const properties = useMemo(() => extractOverridableProperties(template), [template])

  if (!template?.isLayoutBased) return null

  if (properties.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-md bg-muted/50 text-sm text-muted-foreground">
        <Info className="h-4 w-4 shrink-0" />
        <span>This template uses the visual builder. No overridable properties are configured.</span>
      </div>
    )
  }

  const setOverride = (key: string, value: string) => {
    onChange({ ...overrides, [key]: value })
  }

  const clearOverride = (key: string) => {
    const next = { ...overrides }
    delete next[key]
    onChange(next)
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Template Overrides</Label>
      <p className="text-xs text-muted-foreground">
        Customize these properties. Leave blank to use the template default.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {properties.map((prop) => {
          const key = `${prop.elementId}.${prop.property}`
          const value = overrides[key] ?? ''
          const isColorProp = prop.property === 'color' || prop.property === 'backgroundColor'
          return (
            <div key={key} className="space-y-1">
              <Label className="text-xs">{prop.label}</Label>
              {isColorProp ? (
                <div className="flex items-center gap-2">
                  <ColorPicker
                    color={value || prop.currentValue || '#000000'}
                    onChange={(c) => setOverride(key, c)}
                    disabled={disabled}
                  />
                  {value && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] px-2"
                      onClick={() => clearOverride(key)}
                    >Reset</Button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    value={value}
                    onChange={(e) => setOverride(key, e.target.value)}
                    placeholder={prop.currentValue || 'Default'}
                    className="h-8 text-xs"
                    disabled={disabled}
                  />
                  {value && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] px-2"
                      onClick={() => clearOverride(key)}
                    >Reset</Button>
                  )}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">
                Default: {prop.currentValue || '(none)'}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
